import {
  Box,
  CardContent,
  Typography,
  Button,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { useRuns } from "contexts/RunsContext";
import { useSimulations } from "contexts/SimulationsContext";
import { useAuth } from "contexts/AuthContext";
import { useMapState } from "contexts/MapStateContext";
import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { SelectWrapper } from "components/UiComponents";
import useQueryMutation from "hooks/useQueryMutation";
import { useSnackbar } from "notistack";
import usePage from "hooks/usePage";
import KmlFileUploadButton from "./KmlFileUploadButton";
import CustomLcpUploadButton from "./CustomLcpUploadButton";
import { ToggleField } from "./form/ToggleField";
import { NumberField } from "./form/NumberField";

// Valid options for the Landscape Version <Select>. Used to sanitize the value
// when duplicating: a custom-landscape run stores its gs:// path in
// landscape_version, which isn't a selectable option, so we fall back to the
// default (the custom landscape is carried separately via customLcpFile).
const LANDSCAPE_VERSIONS = [
  "220",
  "230",
  "240",
  "250",
  "240XP",
  "250XP",
  "COWRA22",
  "COWRA22_BR1",
];

const DEFAULT_VALUES = {
  simulationName: "DefaultSimulation",
  windSpeed: 25,
  windDirection: 45,
  duration: 240,
  longitude: -122.4194,
  latitude: 37.7749,
  iterations: 10,
  landscapeVersion: "250XP",
  customLcpFile: "",
  customLcpBounds: null,
  moisture1: 0.02,
  moisture10: 0.03,
  moisture100: 0.03,
  moistureLive: 0.6,
  moistureWoody: 0.6,
  useWindNinja: true,
  mitigateVegetationOnly: false,
  vegetationMitigationsNonburnable: false,
  rebuildGraphTiles: false,
  mitigationKmlFile: null,
  mitigationGeojson: null,
  mitigationGeometryId: null,
  additionalBuildingsKmlFile: null,
  additionalBuildingsGeojson: null,
  additionalBuildingsGeometryId: null,
};

export default function CreateUrbanSimulation() {
  const { currentUser } = useAuth();
  const { projectId } = usePage();
  const { clearSimulation, urbanSimToDuplicate, setUrbanSimToDuplicate } =
    useSimulations();
  const { reloadRecentRuns, setIsCreatingSimulation } = useRuns();
  const { enqueueSnackbar } = useSnackbar();
  const { map } = useMapState();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    mode: "onChange",
  });

  const mitigationGeojson = watch("mitigationGeojson");
  const mitigationGeometryId = watch("mitigationGeometryId");
  const additionalBuildingsGeojson = watch("additionalBuildingsGeojson");
  const mitigateVegetationOnly = watch("mitigateVegetationOnly");
  const vegetationMitigationsNonburnable = watch(
    "vegetationMitigationsNonburnable"
  );
  const customLcpFile = watch("customLcpFile");
  const customLcpBounds = watch("customLcpBounds");
  const longitude = watch("longitude");
  const latitude = watch("latitude");

  const mitigationRequired =
    (mitigateVegetationOnly || vegetationMitigationsNonburnable) &&
    !mitigationGeojson &&
    !mitigationGeometryId;

  // When a custom LCP with known bounds is set, the entrypoint must fall
  // inside it — otherwise the simulation has no landscape data at ignition.
  const entrypointOutsideLcp =
    !!customLcpFile &&
    !!customLcpBounds &&
    (Number(longitude) < customLcpBounds.west ||
      Number(longitude) > customLcpBounds.east ||
      Number(latitude) < customLcpBounds.south ||
      Number(latitude) > customLcpBounds.north);

  // Draw (and keep in sync) a rectangle of the custom LCP bounds on the map.
  useEffect(() => {
    if (!map) return;
    const sourceId = "custom-lcp-bounds";
    const fillId = "custom-lcp-bounds-fill";
    const lineId = "custom-lcp-bounds-outline";

    const removeBox = () => {
      try {
        if (map.getLayer(fillId)) map.removeLayer(fillId);
        if (map.getLayer(lineId)) map.removeLayer(lineId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      } catch (e) {
        console.error("Failed to remove custom LCP bounds layer:", e);
      }
    };

    if (!customLcpBounds) {
      removeBox();
      return;
    }

    const { west, south, east, north } = customLcpBounds;
    const polygon = {
      type: "Feature",
      properties: {},
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [west, south],
            [east, south],
            [east, north],
            [west, north],
            [west, south],
          ],
        ],
      },
    };

    try {
      if (map.getSource(sourceId)) {
        map.getSource(sourceId).setData(polygon);
      } else {
        map.addSource(sourceId, { type: "geojson", data: polygon });
        map.addLayer({
          id: fillId,
          type: "fill",
          source: sourceId,
          paint: { "fill-color": "#1976d2", "fill-opacity": 0.08 },
        });
        map.addLayer({
          id: lineId,
          type: "line",
          source: sourceId,
          paint: {
            "line-color": "#1976d2",
            "line-width": 2,
            "line-dasharray": [2, 1],
          },
        });
      }
    } catch (e) {
      console.error("Failed to draw custom LCP bounds layer:", e);
    }

    return removeBox;
  }, [map, customLcpBounds]);

  const combinedFileSizeOkay = (() => {
    const limit = 31.5 * 1024 * 1024;
    let size = 0;
    if (mitigationGeojson)
      size += new TextEncoder().encode(
        JSON.stringify(mitigationGeojson)
      ).length;
    if (additionalBuildingsGeojson)
      size += new TextEncoder().encode(
        JSON.stringify(additionalBuildingsGeojson)
      ).length;
    console.log("Combined file size:", size, "bytes");
    return size <= limit;
  })();

  useEffect(() => {
    if (urbanSimToDuplicate) {
      reset({
        ...DEFAULT_VALUES,
        simulationName:
          urbanSimToDuplicate.name || DEFAULT_VALUES.simulationName,
        duration: urbanSimToDuplicate.duration ?? DEFAULT_VALUES.duration,
        windSpeed: urbanSimToDuplicate.wind_speed ?? DEFAULT_VALUES.windSpeed,
        windDirection:
          urbanSimToDuplicate.wind_direction ?? DEFAULT_VALUES.windDirection,
        longitude: urbanSimToDuplicate.longitude ?? DEFAULT_VALUES.longitude,
        latitude: urbanSimToDuplicate.latitude ?? DEFAULT_VALUES.latitude,
        iterations: urbanSimToDuplicate.iterations ?? DEFAULT_VALUES.iterations,
        landscapeVersion: LANDSCAPE_VERSIONS.includes(
          urbanSimToDuplicate.landscape_version
        )
          ? urbanSimToDuplicate.landscape_version
          : DEFAULT_VALUES.landscapeVersion,
        customLcpFile:
          urbanSimToDuplicate.custom_lcp_file ?? DEFAULT_VALUES.customLcpFile,
        moisture1: urbanSimToDuplicate.moisture_1 ?? DEFAULT_VALUES.moisture1,
        moisture10:
          urbanSimToDuplicate.moisture_10 ?? DEFAULT_VALUES.moisture10,
        moisture100:
          urbanSimToDuplicate.moisture_100 ?? DEFAULT_VALUES.moisture100,
        moistureLive:
          urbanSimToDuplicate.moisture_live ?? DEFAULT_VALUES.moistureLive,
        moistureWoody:
          urbanSimToDuplicate.moisture_woody ?? DEFAULT_VALUES.moistureWoody,
        useWindNinja:
          urbanSimToDuplicate.use_windninja ?? DEFAULT_VALUES.useWindNinja,
        mitigateVegetationOnly:
          urbanSimToDuplicate.mitigate_veg_only ??
          DEFAULT_VALUES.mitigateVegetationOnly,
        vegetationMitigationsNonburnable:
          urbanSimToDuplicate.veg_mitigations_nb ??
          DEFAULT_VALUES.vegetationMitigationsNonburnable,
        rebuildGraphTiles:
          urbanSimToDuplicate.force_compile ?? DEFAULT_VALUES.rebuildGraphTiles,
        mitigationGeometryId: urbanSimToDuplicate.mitigation_id ?? null,
        additionalBuildingsGeometryId:
          urbanSimToDuplicate.additional_buildings_id ?? null,
      });
      setUrbanSimToDuplicate(null);
    }
  }, [urbanSimToDuplicate, setUrbanSimToDuplicate, reset]);

  const { mutate: postSimulation, isPending: isCreating } = useQueryMutation(
    `/simulation`,
    "POST",
    {
      onSuccess: (result) => {
        // A 400 comes back as a pseudo-success result (see useQueryMutation),
        // not a thrown error — surface the server's reason and keep the form
        // open so the user can fix it (e.g. an entrypoint outside the custom
        // landscape bounds) instead of the simulation silently disappearing.
        if (result?.status === 400) {
          enqueueSnackbar(result.detail || "Could not create simulation", {
            variant: "error",
          });
          return;
        }
        enqueueSnackbar("Simulation created successfully", {
          variant: "success",
        });
        reloadRecentRuns();
        setIsCreatingSimulation(false);
      },
    }
  );

  const onSubmit = (data) => {
    const IntermixRequest = {
      study_name: data.simulationName,
      wind_speed: Number(data.windSpeed),
      wind_direction: Number(data.windDirection),
      duration: Number(data.duration),
      init_x: Number(data.longitude),
      init_y: Number(data.latitude),
      iterations: Number(data.iterations),
      point_buffer_distance: null,
      landscape_version: data.landscapeVersion,
      moisture_1: Number(data.moisture1),
      moisture_10: Number(data.moisture10),
      moisture_100: Number(data.moisture100),
      moisture_live: Number(data.moistureLive),
      moisture_woody: Number(data.moistureWoody),
      use_windninja: data.useWindNinja,
      mitigate_veg_only: data.mitigateVegetationOnly,
      veg_mitigations_nb: data.vegetationMitigationsNonburnable,
      force_compile: data.rebuildGraphTiles,
    };
    if (data.customLcpFile && data.customLcpFile.trim()) {
      IntermixRequest.custom_lcp_file = data.customLcpFile.trim();
    }
    if (data.mitigationGeojson) {
      IntermixRequest.mitigate_geometry = data.mitigationGeojson;
    } else if (data.mitigationGeometryId) {
      IntermixRequest.mitigate_geometry_id = data.mitigationGeometryId;
    }
    if (data.additionalBuildingsGeojson) {
      IntermixRequest.additional_buildings_geometry =
        data.additionalBuildingsGeojson;
    } else if (data.additionalBuildingsGeometryId) {
      IntermixRequest.additional_buildings_geometry_id =
        data.additionalBuildingsGeometryId;
    }
    postSimulation({
      simulation_type: "intermix",
      name: data.simulationName,
      description: "Intermix simulation created via UI",
      created_by: currentUser.email,
      xp_project_id: projectId,
      intermix_request: IntermixRequest,
    });
  };

  const disabled =
    isCreating ||
    !isValid ||
    !combinedFileSizeOkay ||
    mitigationRequired ||
    entrypointOutsideLcp;

  return (
    <CardContent
      sx={{ position: "relative", zIndex: 2, backgroundColor: "#fff" }}
    >
      <Typography variant="h6" mb={1}>
        New Simulation
        <Button
          variant="outlined"
          size="small"
          sx={{ float: "right" }}
          onClick={() => {
            reset(DEFAULT_VALUES);
            clearSimulation();
          }}
        >
          Cancel
        </Button>
      </Typography>

      <FormControl fullWidth sx={{ marginBottom: "16px" }}>
        <Controller
          name="simulationName"
          control={control}
          rules={{
            required: "Simulation name is required",
            pattern: {
              value: /^[a-zA-Z0-9_]+$/,
              message:
                "Alphanumeric and underscores only (no spaces or special characters)",
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Simulation Name"
              variant="outlined"
              sx={{ height: "46px", marginBottom: "16px" }}
              placeholder="Enter simulation name"
              onChange={(e) => {
                const value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, "");
                field.onChange(value);
              }}
              error={!!errors.simulationName}
              helperText={errors.simulationName?.message}
            />
          )}
        />
        <Typography variant="caption" color="textSecondary">
          Simulation name must be alphanumeric and can only contain underscores
          (no spaces or special characters).
        </Typography>
      </FormControl>

      <NumberField
        name="windSpeed"
        label="Wind Speed"
        control={control}
        rules={{
          required: true,
          min: { value: 0, message: "Must be ≥ 0" },
          max: { value: 200, message: "Must be ≤ 200" },
        }}
        error={errors.windSpeed}
      />

      <NumberField
        name="windDirection"
        label="Wind Direction"
        control={control}
        rules={{
          required: true,
          min: { value: 0, message: "Must be ≥ 0" },
          max: { value: 360, message: "Must be ≤ 360" },
        }}
        error={errors.windDirection}
      />

      <NumberField
        name="duration"
        label="Duration"
        control={control}
        rules={{
          required: true,
          min: { value: 1, message: "Must be ≥ 1" },
          max: { value: 1080, message: "Must be ≤ 1080" },
        }}
        error={errors.duration}
      />

      <NumberField
        name="longitude"
        label="Initialized Longitude"
        control={control}
        rules={{
          required: true,
          min: { value: -180, message: "Must be ≥ -180" },
          max: { value: 180, message: "Must be ≤ 180" },
        }}
        error={errors.longitude}
      />

      <NumberField
        name="latitude"
        label="Initialized Latitude"
        control={control}
        rules={{
          required: true,
          min: { value: -90, message: "Must be ≥ -90" },
          max: { value: 90, message: "Must be ≤ 90" },
        }}
        error={errors.latitude}
      />

      <NumberField
        name="iterations"
        label="Number of Iterations"
        control={control}
        rules={{
          required: true,
          min: { value: 1, message: "Must be ≥ 1" },
        }}
        error={errors.iterations}
      />

      <SelectWrapper>
        <InputLabel id="select-landscape-version-label">
          Landscape Version
        </InputLabel>
        <Controller
          name="landscapeVersion"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              variant="standard"
              labelId="select-landscape-version-label"
              id="select-landscape-version"
            >
              <MenuItem value={"220"}>Landfire v220 (2020)</MenuItem>
              <MenuItem value={"230"}>Landfire v230 (2022)</MenuItem>
              <MenuItem value={"240"}>Landfire v240 (2023)</MenuItem>
              <MenuItem value={"250"}>Landfire v250 (2024)</MenuItem>
              <MenuItem value={"240XP"}>Landfire v240 XP (2023)</MenuItem>
              <MenuItem value={"250XP"}>Landfire v250 XP (2024)</MenuItem>
              <MenuItem value={"COWRA22"}>Colorado All Lands (2022)</MenuItem>
              <MenuItem value={"COWRA22_BR1"}>
                Colorado All Lands BR (2022)
              </MenuItem>
            </Select>
          )}
        />
      </SelectWrapper>

      <FormControl fullWidth sx={{ marginBottom: "16px" }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <Controller
            name="customLcpFile"
            control={control}
            rules={{
              pattern: {
                value: /^gs:\/\/.+/,
                message: "Must be a GCS path starting with gs://",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  // A manually edited path no longer matches the uploaded
                  // file's bounds, so drop them (clears the map box too).
                  setValue("customLcpBounds", null);
                }}
                fullWidth
                label="Custom Landscape File (optional)"
                variant="outlined"
                placeholder="gs://bucket/path/to/custom.tif"
                error={!!errors.customLcpFile || entrypointOutsideLcp}
                helperText={errors.customLcpFile?.message}
              />
            )}
          />
          <CustomLcpUploadButton
            onUploaded={(data) => {
              setValue("customLcpFile", data.custom_lcp_file, {
                shouldValidate: true,
                shouldDirty: true,
              });
              setValue("customLcpBounds", data.bounds ?? null);
            }}
          />
        </Box>
        <Typography variant="caption" color="textSecondary">
          Optional: upload a pre-built 8-band landscape GeoTIFF (.tif), or paste
          a gs:// path. When set, it overrides the selected Landscape Version.
        </Typography>
        {entrypointOutsideLcp && (
          <FormHelperText error>
            The simulation entrypoint (longitude/latitude) is outside the
            uploaded landscape file's bounds. Move the entrypoint inside the
            highlighted box on the map.
          </FormHelperText>
        )}
      </FormControl>

      <NumberField
        name="moisture1"
        label="1h Fuel Moisture"
        control={control}
        rules={{
          required: true,
          min: { value: 0.01, message: "Must be ≥ 0.01" },
          max: { value: 1.0, message: "Must be ≤ 1.0" },
        }}
        error={errors.moisture1}
        step="0.01"
      />

      <NumberField
        name="moisture10"
        label="10h Fuel Moisture"
        control={control}
        rules={{
          required: true,
          min: { value: 0.01, message: "Must be ≥ 0.01" },
          max: { value: 1.0, message: "Must be ≤ 1.0" },
        }}
        error={errors.moisture10}
        step="0.01"
      />

      <NumberField
        name="moisture100"
        label="100h Fuel Moisture"
        control={control}
        rules={{
          required: true,
          min: { value: 0.01, message: "Must be ≥ 0.01" },
          max: { value: 1.0, message: "Must be ≤ 1.0" },
        }}
        error={errors.moisture100}
        step="0.01"
      />

      <NumberField
        name="moistureLive"
        label="Live Fuel Moisture"
        control={control}
        rules={{
          required: true,
          min: { value: 0.01, message: "Must be ≥ 0.01" },
          max: { value: 2.0, message: "Must be ≤ 2.0" },
        }}
        error={errors.moistureLive}
        step="0.01"
      />

      <NumberField
        name="moistureWoody"
        label="Woody Fuel Moisture"
        control={control}
        rules={{
          required: true,
          min: { value: 0.01, message: "Must be ≥ 0.01" },
          max: { value: 2.0, message: "Must be ≤ 2.0" },
        }}
        error={errors.moistureWoody}
        step="0.01"
      />

      <ToggleField
        name="useWindNinja"
        label="Use Wind Ninja"
        control={control}
      />
      <ToggleField
        name="mitigateVegetationOnly"
        label="Mitigate Vegetation Only"
        control={control}
      />
      <ToggleField
        name="vegetationMitigationsNonburnable"
        label="Vegetation Mitigations Nonburnable"
        control={control}
      />
      <ToggleField
        name="rebuildGraphTiles"
        label="Rebuild Graph Tiles / Force Compile"
        control={control}
      />

      <Controller
        name="mitigationKmlFile"
        control={control}
        render={({ field }) => (
          <KmlFileUploadButton
            label="Mitigation Geometries"
            file={field.value}
            setFile={(f) => field.onChange(f)}
            setGeojson={(g) => setValue("mitigationGeojson", g)}
          />
        )}
      />
      {mitigationRequired && (
        <FormHelperText error>
          A mitigation geometry is required when Mitigate Vegetation Only or
          Vegetation Mitigations Nonburnable is enabled.
        </FormHelperText>
      )}

      <Controller
        name="additionalBuildingsKmlFile"
        control={control}
        render={({ field }) => (
          <KmlFileUploadButton
            label="Building Geometries"
            file={field.value}
            setFile={(f) => field.onChange(f)}
            setGeojson={(g) => setValue("additionalBuildingsGeojson", g)}
          />
        )}
      />

      {!combinedFileSizeOkay && (
        <FormHelperText error>
          Input files exceed the 32 MB size limit.
        </FormHelperText>
      )}

      <FormControl fullWidth>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={disabled}
          sx={{ marginTop: 2 }}
        >
          {isCreating ? "Creating..." : "Create Simulation"}
        </Button>
      </FormControl>
    </CardContent>
  );
}
